import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle,
  BookOpen,
  Clock,
  UserCheck,
  ClipboardList,
  FileText,
  Globe,
  Users,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
} from "lucide-react";
import TopNavbar from "../components/Nav/TopNavbar";

import Contact from "../components/Sections/Footer";
import Languages from "../components/Mocks/Language";
import { onSubmit } from "../components/Mocks/EmailSender";

// UI Components
function Card({ children, className }) {
  return (
    <div className={`bg-white shadow-md rounded-lg p-4 ${className}`}>
      {children}
    </div>
  );
}

function CardHeader({ children }) {
  return <div className="border-b pb-2 mb-2">{children}</div>;
}

// function CardTitle({ children }) {
//   return <h2 className="text-xl font-bold">{children}</h2>;
// }

function CardContent({ children }) {
  return <div>{children}</div>;
}

function Badge({ children, className }) {
  return (
    <span className={`px-3 py-1 rounded-full text-white text-sm ${className}`}>
      {children}
    </span>
  );
}

function Button({ children, className, ...props }) {
  return (
    <button
      className={`px-4 py-2 rounded-md font-semibold ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

const FieldDetailsSearch = () => {
  const { id } = useParams(); // Get the value from the URL
  const [field, setField] = useState(null);
  const topRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    tel: "",
    email: "",
    adresse: "",
    raisonSociale: "",
    motivation: "",
    formation: id || "", // Include the formation ID in the form data
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form Data:", formData);
    onSubmit(formData); // Call the onSubmit function to handle form submission
    // TODO: send to backend
    setIsOpen(false); // Close modal after submission
  };

  useEffect(() => {
    // Scroll to the top of the page or component
    window.scrollTo(0, 0);
    function searchCourseByTitle(title) {
      return Languages.courses.find(
        (course) => course.title.toLowerCase() === title.toLowerCase()
      );
    }
    if (id) setField(searchCourseByTitle(id));
  }, [id]); // Empty dependency array ensures this only runs once on mount

  if (!field) {
    return (
      <div>
        <TopNavbar />
        <h1 style={{ color: "rgb(255, 153, 0)" }}>VALACADEMY FORMATIONS </h1>
        <div style={{ marginTop: "10%" }}></div>
        <p className="text-center text-red-500">Formation non trouvée.</p>;
        <div style={{ marginTop: "10%" }}></div>
        <Contact />
      </div>
    );
  }
  return (
    <>
      <div ref={topRef} className="max-w-4xl mx-auto p-6">
        <TopNavbar />
        <h1 style={{ color: "rgb(255, 153, 0)" }}>VALACADEMY FORMATIONS</h1>

        <div style={{ marginTop: "10%" }}></div>
        <Card className="shadow-lg rounded-2xl p-6">
          <CardHeader>
            <div className="text-3xl font-bold flex items-center gap-2 mb-4">
              <BookOpen size={28} className="text-blue-500" />
              <span>{field.title}</span>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4 flex items-center gap-2">
              <Globe size={20} className="text-purple-500" />
              <strong>Accessibilité:</strong> {field.accessibility}
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-100 p-4 rounded-lg flex items-center gap-2">
                <Clock size={20} className="text-green-500" />
                <div>
                  <p className="font-semibold">Durée :</p>
                  <p className="text-gray-700">{field.duration}</p>
                </div>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg flex items-center gap-2">
                <UserCheck size={20} className="text-yellow-500" />
                <div>
                  <p className="font-semibold">Prérequis :</p>
                  <p className="text-gray-700">{field.prerequisites}</p>
                </div>
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-3">Objectifs :</h3>
            <ul className="space-y-2 mb-6">
              {field.objectives.map((objective, index) => (
                <li
                  key={index}
                  className="flex items-center gap-2 text-gray-700"
                >
                  <CheckCircle className="text-green-500" size={18} />{" "}
                  {objective}
                </li>
              ))}
            </ul>
            <h3 className="text-xl font-semibold mb-3">Contenu du cours :</h3>
            <div
              className={`grid md:grid-cols-${Math.min(
                4,
                Math.round(field.content?.length / 9)
              )} gap-4 mb-6`}
            >
              {field.content?.map((item, index) => (
                <p
                  key={index}
                  className="text-gray-700 flex items-center gap-2"
                >
                  <ClipboardList size={18} className="text-blue-500" /> {item}
                </p>
              ))}
            </div>
            <h3 className="text-xl font-semibold mb-3">
              Méthodes pédagogiques :
            </h3>
            <div className="flex flex-wrap gap-2">
              {field.methods?.map((method, index) => (
                <Badge key={index} className="bg-blue-500 text-white">
                  {method}
                </Badge>
              ))}
            </div>
            <h3 className="text-xl font-semibold mt-6 mb-3">Évaluation :</h3>
            <ul className="space-y-2 mb-6">
              {field.evaluation?.map((evalItem, index) => (
                <li
                  key={index}
                  className="text-gray-700 flex items-center gap-2"
                >
                  <FileText size={18} className="text-red-500" /> {evalItem}
                </li>
              ))}
            </ul>
            <h3 className="text-xl font-semibold mt-6 mb-3">
              Matériel requis :
            </h3>
            <ul className="space-y-2 mb-6">
              {field.materials?.map((material, index) => (
                <li
                  key={index}
                  className="text-gray-700 flex items-center gap-2"
                >
                  <Users size={18} className="text-teal-500" /> {material}
                </li>
              ))}
            </ul>
            <p className="text-gray-600 mt-4 flex items-center gap-2">
              <UserCheck size={20} className="text-green-500" />
              <strong>Formateur :</strong> {field.trainer}
            </p>
            <div className="mt-6">
              <Button
                onClick={() => setIsOpen(true)}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg"
              >
                Je m'inscris
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            style={{ maxHeight: "80vh", marginTop: "5%" }}
            className="overflow-y-auto bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 relative"
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-red-600 text-xl font-bold"
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">
              Inscription à la formation
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label className="block font-medium flex items-center">
                    <User className="text-blue-600 mr-2" size={20} />
                    Nom
                  </label>
                  <input
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block font-medium flex items-center">
                    <User className="text-blue-600 mr-2" size={20} />
                    Prénom
                  </label>
                  <input
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>
              </div>

              {/* Telephone and Adresse on the same line */}
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label className="block font-medium flex items-center">
                    <Phone className="text-red-600 mr-2" size={20} />
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="tel"
                    value={formData.tel}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block font-medium flex items-center">
                    <MapPin className="text-purple-600 mr-2" size={20} />
                    Adresse
                  </label>
                  <input
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block font-medium flex items-center">
                  <Mail className="text-orange-600 mr-2" size={20} />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block font-medium flex items-center">
                  <Briefcase className="text-teal-600 mr-2" size={20} />
                  Raison sociale
                </label>
                <input
                  name="raisonSociale"
                  value={formData.raisonSociale}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block font-medium flex items-center">
                  <FileText className="text-yellow-600 mr-2" size={20} />
                  Motivation ou Objectif
                </label>
                <textarea
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold"
                >
                  Envoyer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Contact />
    </>
  );
};

export default FieldDetailsSearch;
